"use client";

import {
	type FC,
	type SubmitEventHandler,
	startTransition,
	use,
	useEffect,
	useState,
} from "react";
import { useSearchParams } from "next/navigation";
import { useTransitionRouter } from "next-view-transitions";
import posthog from "posthog-js";
import { track } from "@vercel/analytics";
import isUrl from "is-url";

import type {
	FlashcardSetInfo,
	GistInfo,
	HttpError,
	NoteInfo,
	QuizInfo,
	UserDetails,
} from "@vita-learning/types";

import {
	LoginForm,
	type LoginFormOnSubmitProps,
} from "~/client/components/forms/LoginForm";
import { usePopupQueue } from "~/client/components/portals/Popup";
import {
	useFlashcardSetsStore,
	useGistsStore,
	useNotesStore,
	useQuizzesStore,
} from "~/client/components/stores";
import { loginUser } from "~/client/services";
import { increment, makeUrl } from "~/shared/utils";

import {
	makeHandleHttpError,
	makePosthogEventName as ph,
	updateUserFlags,
	userToPostHogUser,
} from "~/shared/services";

import { useImportPreviewResource } from "../useImportPreviewResource";

interface LoginProps {
	readonly verifiedPromise: Promise<boolean>;

	readonly seedData: {
		hs2711: {
			flashcardSets: Omit<FlashcardSetInfo, "userId">[];
			gists: Omit<GistInfo, "userId">[];
			notes: Omit<NoteInfo, "userId">[];
			quizzes: Omit<QuizInfo, "userId">[];
		};
		hs3801: {
			quizzes: Omit<QuizInfo, "userId">[];
		};
		bio1002: {
			flashcardSets: Omit<FlashcardSetInfo, "userId">[];
			gists: Omit<GistInfo, "userId">[];
			notes: Omit<NoteInfo, "userId">[];
			quizzes: Omit<QuizInfo, "userId">[];
		};
		// biome-ignore lint/complexity/noBannedTypes: Placeholder for future seed data
		chem1302: {
			// TODO: Add seed data for chem1302
		};
	};
}

export const Login: FC<LoginProps> = ({
	verifiedPromise,
	seedData,
}: LoginProps) => {
	const verified = use(verifiedPromise);

	const router = useTransitionRouter();
	const searchParams = useSearchParams();

	const [isLoading, setIsLoading] = useState(false);
	const [loginAttempts, setLoginAttempts] = useState(0);
	const [error, setError] = useState<HttpError | null>(null);

	const { enqueueSuccess, enqueueError, enqueueWarning } = usePopupQueue();

	const createManyFlashcardSets = useFlashcardSetsStore(s => s.createMany);
	const createManyGists = useGistsStore(s => s.createMany);
	const createManyNotes = useNotesStore(s => s.createMany);
	const createManyQuizzes = useQuizzesStore(s => s.createMany);

	const importPreviewResource = useImportPreviewResource();

	const referrer = searchParams?.get("referrer");
	const utmCampaign = searchParams?.get("utm_campaign");

	const makeHandleSubmit = ({
		isEnabled,
		email,
		password,
	}: LoginFormOnSubmitProps) => {
		const handleSubmit: SubmitEventHandler<HTMLFormElement> = async e => {
			e.preventDefault();
			setIsLoading(true);

			try {
				if (!isEnabled) return;

				const user = await loginUser(
					{ email, password },
					{ provider: "credentials" }
				);

				const message = `Welcome ${!verified ? "back, " : ""} ${user.name.first}! 😃`;
				const url = referrer ?? "/notes";

				if (
					utmCampaign?.endsWith("midterm_winter2026") &&
					!user.flags.campaigns.includes(
						`uwo_${utmCampaign}` as UserDetails["flags"]["campaigns"][number]
					)
				) {
					switch (utmCampaign) {
						case "hs2711_midterm_winter2026": {
							await Promise.allSettled([
								createManyFlashcardSets(
									seedData.hs2711.flashcardSets.map(
										f => ({ ...f, userId: user.id }) as FlashcardSetInfo
									)
								),
								createManyNotes(
									seedData.hs2711.notes.map(
										n => ({ ...n, userId: user.id }) as NoteInfo
									)
								),
								createManyGists(
									seedData.hs2711.gists.map(
										g => ({ ...g, userId: user.id }) as GistInfo
									)
								),
								createManyQuizzes(
									seedData.hs2711.quizzes.map(
										q => ({ ...q, userId: user.id }) as QuizInfo
									)
								),
							]);
							break;
						}
						case "hs3801_midterm_winter2026": {
							await createManyQuizzes(
								seedData.hs3801.quizzes.map(
									q => ({ ...q, userId: user.id }) as QuizInfo
								)
							).catch(() => {});
							break;
						}
						case "bio1002_midterm_winter2026":{
								await Promise.allSettled([
									createManyFlashcardSets(
										seedData.bio1002.flashcardSets.map(
											f => ({ ...f, userId: user.id }) as FlashcardSetInfo
										)
									),
									createManyNotes(
										seedData.bio1002.notes.map(
											n => ({ ...n, userId: user.id }) as NoteInfo
										)
									),
									createManyGists(
										seedData.bio1002.gists.map(
											g => ({ ...g, userId: user.id }) as GistInfo
										)
									),
									createManyQuizzes(
										seedData.bio1002.quizzes.map(
											q => ({ ...q, userId: user.id }) as QuizInfo
										)
									),
								]);
								break;
						}
						case "chem1302_midterm_winter2026":
							// No seed data for these campaigns yet
							break;
						default:
							break;
					}

					posthog.capture(ph("login_flow", "study_kit", "created"), {
						utm_medium: searchParams?.get("utm_medium"),
						utm_source: searchParams?.get("utm_source"),
						utm_campaign: utmCampaign,
					});

					await updateUserFlags({
						campaigns: [
							...user.flags.campaigns,
							`uwo_${utmCampaign}` as UserDetails["flags"]["campaigns"][number],
						],
					});
				}

				await importPreviewResource(user);

				enqueueSuccess(message, "Login");
				posthog.identify(user.id, userToPostHogUser(user));
				posthog.capture(ph("login_flow", "user", "logged_in"));
				track("Login", { email: user.email });

				router.replace(url);
			} catch (e) {
				const handleHttpError = makeHandleHttpError("LOGIN_ERROR");
				const error = handleHttpError(e);

				setError(error);

				if (
					!error.id ||
					![
						"LOGIN_EMAIL",
						"LOGIN_PASSWORD",
						"LOGIN_EMAIL_NOT_VERIFIED",
						"LOGIN_EMAIL_NOT_ONBOARDED",
					].some(e => e === error.id)
				)
					enqueueError(error.message);

				if (error.id === "LOGIN_EMAIL_NOT_ONBOARDED") {
					const url = makeUrl("/onboarding");

					enqueueWarning(`${error.message}. Redirecting...`, "Attention");
					setTimeout(() => router.push(url.pathname + url.search), 2000);
				}
				if (error.id === "LOGIN_EMAIL_NOT_VERIFIED") {
					const url = makeUrl("/verify-email");
					url.searchParams.set(
						"error",
						"Check your email inbox for the verification email, or enter your email address below to receive a new verification email."
					);

					if (email) url.searchParams.set("email", email);

					enqueueWarning(`${error.message}. Redirecting...`, "Attention");
					setTimeout(() => router.push(url.pathname + url.search), 2000);
				}
			} finally {
				setTimeout(() => setIsLoading(false), 500);
				startTransition(() => {
					setLoginAttempts(increment);
				});
			}
		};

		return handleSubmit;
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only re-run when 'referrer' changes
	useEffect(() => {
		if (!referrer || !isUrl(referrer)) return;

		const url = makeUrl(referrer);

		router.prefetch(url.pathname);
	}, [referrer]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Only re-run when 'verified' changes
	useEffect(() => {
		if (!verified) return;

		enqueueSuccess("Email verified!", "Success");
	}, [verified]);

	useEffect(() => {
		const _id = searchParams?.get("_id");

		if (!_id) return;

		posthog.identify(_id);
	}, [searchParams]);

	return (
		<LoginForm
			className="auth__form"
			error={error}
			isLoading={isLoading}
			makeOnSubmit={makeHandleSubmit}
			loginAttempts={loginAttempts}
		/>
	);
};
